import * as React from 'react';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import clsx from 'clsx';
import { addOpacity } from 'lib/helpers';
import { InnerPageLayoutBottom } from './InnerPageLayoutBottom';
import { InnerPageLayoutContent } from './InnerPageLayoutContent';

const PREFIX = 'InnerPageLayout';

const classes = {
    contentShadow: `${PREFIX}contentShadow`,
    contentSpacing: `${PREFIX}contentSpacing`,
    contentWrapper: `${PREFIX}contentWrapper`,
    content: `${PREFIX}content`,
    contentDisabled: `${PREFIX}contentDisabled`
};

const StyledBox = styled(Box, {
    shouldForwardProp: prop => prop !== 'disableContentSpaces'
})<{ disableContentSpaces: boolean }>(({ theme, disableContentSpaces }) => ({
    [`& .${classes.contentShadow}`]: {
        boxShadow: `1px -5px 8px ${addOpacity(theme.palette.text.primary, 0.12)}`
    },

    [`& .${classes.contentSpacing}`]: {
        padding: theme.spacing(0, 2, 2, 2)
    },

    [`& .${classes.contentWrapper}`]: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
    },

    [`& .${classes.content}`]: {
        overflowY: 'scroll',
        flex: 1,
        marginTop: !disableContentSpaces ? theme.spacing(2) : 0
    },

    [`& .${classes.contentDisabled}`]: {
        opacity: 0.4,
        pointerEvents: 'none'
    }
}));

interface IProps {
    disabled?: boolean;
    disableContentSpaces?: boolean;
    className?: string;
}

export const InnerPageLayout: React.FC<React.PropsWithChildren<IProps>> = ({
    children,
    disableContentSpaces,
    disabled,
    className
}) => {
    const [isBottomFloating, setIsBottomFloating] = React.useState(false);
    const contentRef = React.useRef<HTMLDivElement>(null);
    const contentScrollerRef = React.useRef<HTMLDivElement>(null);
    const content = React.useMemo(
        () =>
            React.Children.toArray(children).find(
                child => (child as React.ReactElement).type === InnerPageLayoutContent
            ),
        [children]
    );
    const bottom = React.useMemo(
        () =>
            React.Children.toArray(children).find(
                child => (child as React.ReactElement).type === InnerPageLayoutBottom
            ),
        [children]
    );
    const unkownChildren = React.useMemo(
        () =>
            React.Children.toArray(children).filter(
                child =>
                    (child as React.ReactElement).type !== InnerPageLayoutBottom &&
                    (child as React.ReactElement).type !== InnerPageLayoutContent
            ),
        [children]
    );

    const handleScrollableDetect = React.useCallback(() => {
        if (contentScrollerRef.current) {
            if (contentScrollerRef.current.clientHeight < contentScrollerRef.current.scrollHeight) {
                setIsBottomFloating(true);
            }
        }
    }, []);

    React.useEffect(handleScrollableDetect, [handleScrollableDetect]);
    React.useEffect(() => {
        if (!contentRef.current) {
            return;
        }
        const observer = new MutationObserver(handleScrollableDetect);
        observer.observe(contentRef.current, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, [handleScrollableDetect]);

    const hasBottomContent = React.useMemo(
        () => bottom && React.Children.toArray((bottom as any).props.children).some(element => !!element),
        [bottom]
    );

    return (
        <StyledBox
            disableContentSpaces={!!disableContentSpaces}
            display="flex"
            flexDirection="column"
            height="100%"
            className={className}
        >
            {unkownChildren}
            {content && (
                <div
                    className={clsx(classes.contentSpacing, classes.content, {
                        [classes.contentDisabled]: disabled
                    })}
                    ref={contentScrollerRef}
                >
                    <div ref={contentRef} className={classes.contentWrapper}>
                        {content}
                    </div>
                </div>
            )}
            {bottom && hasBottomContent && (
                <Box
                    className={clsx({ [classes.contentShadow]: isBottomFloating })}
                    padding={2}
                    position="relative"
                    marginTop="auto"
                >
                    {bottom}
                </Box>
            )}
        </StyledBox>
    );
};
